import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { roleLabel, roleColor, situacaoEleitLabel, situacaoEleitColor,
         validacaoWALabel, validacaoWAColor, limparTelefone, gerarMsgVerificacao } from '../utils/helpers';
import AppShell   from '../components/AppShell';
import BotWhatsApp from '../components/BotWhatsApp';

const TABS = [
  { id: 'usuarios', label: '👥 Usuários'       },
  { id: 'bot',      label: '🤖 Bot WhatsApp'   },
  { id: 'pesquisa', label: '📋 Pesquisa Eleit.' },
];

// ── Helper: botão de copiar ───────────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} title="Copiar"
      style={{ background:'none', border:'none', cursor:'pointer', fontSize:13,
        color: copied ? '#057a55' : '#888', padding:'0 4px' }}>
      {copied ? '✅' : '📋'}
    </button>
  );
}

// ── Modal zona eleitoral ──────────────────────────────────────────────────────
function ZonaModal({ user, onClose, onSaved }) {
  const [zona, setZona]   = useState(user.zona_eleitoral || '');
  const [secao, setSecao] = useState(user.secao_votacao  || '');
  const [saving, setSaving] = useState(false);
  const [erro, setErro]     = useState('');

  const salvar = async () => {
    if (!zona || !secao) { setErro('Preencha zona e seção'); return; }
    setSaving(true); setErro('');
    try {
      await axios.patch(`/api/users/${user.id}/situacao-eleitoral`, {
        situacao_eleitoral: 'ATIVO',
        zona_eleitoral: zona.trim(),
        secao_votacao:  secao.trim(),
      });
      onSaved();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth:400 }}>
        <div className="modal-header">
          <h3 className="modal-title">🗳️ Dados Eleitorais</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize:13, color:'#555', marginBottom:16 }}>
          Para ativar o título de <strong>{user.nome}</strong>, informe:
        </p>
        <div className="field-group">
          <label className="r-label">ZONA ELEITORAL</label>
          <input className="r-input" value={zona} onChange={e => setZona(e.target.value)}
            placeholder="Ex: 001" maxLength={10} />
        </div>
        <div className="field-group" style={{ marginTop:12 }}>
          <label className="r-label">SEÇÃO DE VOTAÇÃO</label>
          <input className="r-input" value={secao} onChange={e => setSecao(e.target.value)}
            placeholder="Ex: 0042" maxLength={10} />
        </div>
        {erro && <div className="alert alert-danger" style={{ marginTop:12 }}>{erro}</div>}
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={salvar} disabled={saving} className="btn" style={{ flex:1 }}>
            {saving ? 'Salvando...' : '✅ Confirmar e Ativar'}
          </button>
          <button onClick={onClose} className="btn btn-outline"
            style={{ borderColor:'var(--danger)', color:'var(--danger)' }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AuxiliarPage() {
  const { user, logout } = useAuth();
  const [tab,     setTab]     = useState('usuarios');
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSit,  setFilterSit]  = useState('');
  const [zonaModal,  setZonaModal]  = useState(null); // user a editar

  // Pesquisa
  const [pesquisas,      setPesquisas]      = useState([]);
  const [novaPesquisa,   setNovaPesquisa]   = useState({ titulo:'', mensagem:'' });
  const [pesquisaSel,    setPesquisaSel]    = useState(null);
  const [respostas,      setRespostas]      = useState([]);
  const [enviandoPes,    setEnviandoPes]    = useState(false);
  const [pesquisaTab,    setPesquisaTab]    = useState('lista');
  const [userEnvio,      setUserEnvio]      = useState('');
  const [pesquisaEnvioId,setPesquisaEnvioId]= useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get('/api/users'); setUsers(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchPesquisas = useCallback(async () => {
    try { const r = await axios.get('/api/pesquisa'); setPesquisas(r.data); }
    catch {}
  }, []);

  useEffect(() => { fetchUsers(); fetchPesquisas(); }, [fetchUsers, fetchPesquisas]);

  const fetchRespostas = async (pesquisaId) => {
    try { const r = await axios.get(`/api/pesquisa/${pesquisaId}/respostas`); setRespostas(r.data); }
    catch {}
  };

  const changeSitEl = async (u, val) => {
    if (val === 'ATIVO') { setZonaModal(u); return; }
    try {
      await axios.patch(`/api/users/${u.id}/situacao-eleitoral`, { situacao_eleitoral: val });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  const criarPesquisa = async () => {
    if (!novaPesquisa.titulo || !novaPesquisa.mensagem)
      return alert('Preencha título e mensagem');
    try {
      await axios.post('/api/pesquisa', novaPesquisa);
      setNovaPesquisa({ titulo:'', mensagem:'' });
      fetchPesquisas();
      alert('✅ Pesquisa criada!');
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  const enviarPesquisa = async () => {
    if (!userEnvio || !pesquisaEnvioId) return alert('Selecione usuário e pesquisa');
    const u = users.find(x => x.id === userEnvio);
    if (!u?.telefone) return alert('Usuário sem telefone cadastrado');
    setEnviandoPes(true);
    try {
      await axios.post('/api/pesquisa/enviar', {
        user_id: u.id, telefone: u.telefone, pesquisa_id: pesquisaEnvioId
      });
      // Envia via WhatsApp
      const pesquisa = pesquisas.find(p => p.id === pesquisaEnvioId);
      await axios.post('/api/whatsapp/enviar', {
        user_id: u.id, telefone: u.telefone, mensagem: pesquisa.mensagem
      });
      alert(`✅ Pesquisa enviada para ${u.nome}`);
    } catch (e) { alert(e.response?.data?.error || 'Erro ao enviar'); }
    finally { setEnviandoPes(false); }
  };

  const filtered = users.filter(u =>
    u.role !== 'ADMIN' && u.role !== 'AUXILIAR_ADM' &&
    (!search     || u.nome?.includes(search.toUpperCase()) || u.cpf?.includes(search) || u.login?.includes(search)) &&
    (!filterRole || u.role === filterRole) &&
    (!filterSit  || u.situacao_eleitoral === filterSit)
  );

  const sideBottom = (
    <button onClick={logout} className="btn btn-outline btn-sm"
      style={{ width:'100%', borderColor:'var(--danger)', color:'var(--danger)' }}>Sair</button>
  );

  return (
    <AppShell tabs={TABS} activeTab={tab} onTabChange={setTab}
      roleBadge="🛡 AUXILIAR ADM" userName={user?.nome || user?.login}
      sideBottom={sideBottom}>

      {zonaModal && (
        <ZonaModal user={zonaModal} onClose={() => setZonaModal(null)}
          onSaved={() => { setZonaModal(null); fetchUsers(); }} />
      )}

      {loading && <div className="loading-screen"><div className="spinner" /> Carregando...</div>}

      {/* ── ABA USUÁRIOS ── */}
      {!loading && tab === 'usuarios' && (
        <div>
          <h2 className="page-title">👥 Usuários Cadastrados</h2>

          {/* Filtros */}
          <div className="filter-bar">
            <input className="r-input" placeholder="🔍 Buscar nome, CPF, login..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ maxWidth:260 }} />
            <select className="r-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
              <option value="">Todos os perfis</option>
              <option value="COORDENADOR_GERAL">Coord. Geral</option>
              <option value="COORDENADOR_MUNICIPAL">Coord. Municipal</option>
              <option value="LIDERANCA">Liderança</option>
              <option value="APOIADOR">Apoiador</option>
            </select>
            <select className="r-select" value={filterSit} onChange={e => setFilterSit(e.target.value)}>
              <option value="">Sit. Eleitoral</option>
              <option value="EM_ANALISE">Em Análise</option>
              <option value="IRREGULAR">Irregular</option>
              <option value="ATIVO">Ativo</option>
            </select>
            <span style={{ fontSize:12, color:'#888' }}>{filtered.length} usuário(s)</span>
          </div>

          <div className="table-wrap" style={{ marginTop:16 }}>
            <table className="r-table">
              <thead>
                <tr>
                  <th>Nome</th><th>CPF</th><th>Dt. Nasc.</th><th>Mãe</th>
                  <th>Telefone</th><th>Município</th><th>Perfil</th>
                  <th>Sit. Eleitoral</th><th>Zona</th><th>Seção</th>
                  <th>WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    {/* Nome com copiar */}
                    <td style={{ whiteSpace:'nowrap' }}>
                      <span style={{ fontSize:13 }}>{u.nome}</span>
                      <CopyBtn text={u.nome} />
                    </td>
                    {/* CPF com copiar */}
                    <td style={{ whiteSpace:'nowrap' }}>
                      <span style={{ fontFamily:'monospace', fontSize:12 }}>{u.cpf}</span>
                      <CopyBtn text={u.cpf} />
                    </td>
                    {/* Data nascimento com copiar */}
                    <td style={{ whiteSpace:'nowrap' }}>
                      <span style={{ fontSize:12 }}>
                        {u.data_nascimento ? new Date(u.data_nascimento).toLocaleDateString('pt-BR') : '—'}
                      </span>
                      {u.data_nascimento && <CopyBtn text={new Date(u.data_nascimento).toLocaleDateString('pt-BR')} />}
                    </td>
                    {/* Nome da mãe com copiar */}
                    <td style={{ whiteSpace:'nowrap' }}>
                      <span style={{ fontSize:12 }}>{u.nome_mae || '—'}</span>
                      {u.nome_mae && <CopyBtn text={u.nome_mae} />}
                    </td>
                    <td style={{ fontSize:12 }}>{u.telefone || '—'}</td>
                    <td style={{ fontSize:12 }}>{u.municipio || '—'}</td>
                    <td>
                      <span className="pill" style={{ background: roleColor(u.role)+'22', color: roleColor(u.role), fontSize:11 }}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    {/* Situação eleitoral — editável */}
                    <td>
                      <select
                        className="r-select"
                        value={u.situacao_eleitoral}
                        onChange={e => changeSitEl(u, e.target.value)}
                        style={{ fontSize:11, padding:'3px 6px',
                          color: situacaoEleitColor(u.situacao_eleitoral),
                          borderColor: situacaoEleitColor(u.situacao_eleitoral) }}>
                        <option value="EM_ANALISE">🔍 Em Análise</option>
                        <option value="IRREGULAR">⚠️ Irregular</option>
                        <option value="ATIVO">✅ Ativo</option>
                      </select>
                    </td>
                    <td style={{ fontSize:12, textAlign:'center' }}>{u.zona_eleitoral || '—'}</td>
                    <td style={{ fontSize:12, textAlign:'center' }}>{u.secao_votacao  || '—'}</td>
                    <td>
                      <span className="pill" style={{
                        background: validacaoWAColor(u.validacao_whatsapp)+'22',
                        color: validacaoWAColor(u.validacao_whatsapp), fontSize:11 }}>
                        {validacaoWALabel(u.validacao_whatsapp)}
                      </span>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={11} style={{ textAlign:'center', color:'#aaa', padding:32 }}>Nenhum usuário encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ABA BOT WHATSAPP ── */}
      {!loading && tab === 'bot' && (
        <div>
          <h2 className="page-title">🤖 Bot WhatsApp</h2>
          <BotWhatsApp users={users} coordId={null} coordsGerais={[]} />
        </div>
      )}

      {/* ── ABA PESQUISA ── */}
      {!loading && tab === 'pesquisa' && (
        <div>
          <h2 className="page-title">📋 Pesquisa Eleitoral</h2>

          {/* Sub-tabs */}
          <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid #e5e5e5' }}>
            {[['lista','📋 Pesquisas'],['nova','➕ Nova Pesquisa'],['enviar','📤 Enviar'],['respostas','📊 Respostas']].map(([id, label]) => (
              <button key={id} onClick={() => setPesquisaTab(id)}
                style={{ padding:'8px 16px', border:'none', background:'none', cursor:'pointer',
                  fontWeight: pesquisaTab===id ? 700 : 400,
                  color: pesquisaTab===id ? 'var(--primary)' : '#777',
                  borderBottom: pesquisaTab===id ? '2px solid var(--primary)' : '2px solid transparent',
                  marginBottom:-2, fontSize:13 }}>
                {label}
              </button>
            ))}
          </div>

          {/* Lista de pesquisas */}
          {pesquisaTab === 'lista' && (
            <div>
              {pesquisas.length === 0 && (
                <div style={{ textAlign:'center', color:'#aaa', padding:40 }}>
                  Nenhuma pesquisa criada. Clique em "Nova Pesquisa".
                </div>
              )}
              {pesquisas.map(p => (
                <div key={p.id} className="card" style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                    <div>
                      <div style={{ fontWeight:700, color:'var(--primary)', marginBottom:4 }}>{p.titulo}</div>
                      <div style={{ fontSize:13, color:'#555', maxWidth:600, lineHeight:1.6 }}>{p.mensagem}</div>
                      <div style={{ fontSize:11, color:'#999', marginTop:6 }}>
                        Criada por {p.criado_por_nome || '—'} · {new Date(p.created_at).toLocaleDateString('pt-BR')} ·{' '}
                        <strong>{p.total_respostas}</strong> resposta(s)
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100,
                        background: p.ativa ? '#05785522' : '#f0f0f0',
                        color: p.ativa ? '#057a55' : '#999', fontWeight:700 }}>
                        {p.ativa ? '✅ Ativa' : '⏸ Inativa'}
                      </span>
                      <button className="btn btn-sm btn-light"
                        onClick={async () => {
                          await axios.patch(`/api/pesquisa/${p.id}/ativar`, { ativa: !p.ativa });
                          fetchPesquisas();
                        }}>
                        {p.ativa ? 'Pausar' : 'Ativar'}
                      </button>
                      <button className="btn btn-sm"
                        onClick={() => { setPesquisaSel(p.id); fetchRespostas(p.id); setPesquisaTab('respostas'); }}>
                        Ver respostas
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Nova pesquisa */}
          {pesquisaTab === 'nova' && (
            <div className="card" style={{ maxWidth:600 }}>
              <h3 className="card-title">➕ Criar Nova Pesquisa</h3>
              <div className="field-group" style={{ marginTop:16 }}>
                <label className="r-label">TÍTULO DA PESQUISA</label>
                <input className="r-input" value={novaPesquisa.titulo}
                  onChange={e => setNovaPesquisa(p => ({...p, titulo: e.target.value}))}
                  placeholder="Ex: Pesquisa de intenção de voto" maxLength={255} />
              </div>
              <div className="field-group" style={{ marginTop:12 }}>
                <label className="r-label">MENSAGEM A ENVIAR</label>
                <textarea className="r-input r-textarea" rows={5}
                  value={novaPesquisa.mensagem}
                  onChange={e => setNovaPesquisa(p => ({...p, mensagem: e.target.value}))}
                  placeholder="Digite a mensagem da pesquisa. O colaborador responderá por WhatsApp e a resposta será registrada automaticamente."
                  maxLength={1000} style={{ resize:'vertical', fontFamily:'inherit' }} />
                <span style={{ fontSize:11, color:'#aaa' }}>{novaPesquisa.mensagem.length}/1000</span>
              </div>
              <div style={{ marginTop:4, padding:'10px 12px', background:'#f0f4ff', borderRadius:8, fontSize:12, color:'#000080' }}>
                💡 A resposta do colaborador será registrada automaticamente pelo Bot WhatsApp. Certifique-se de que o bot está conectado antes de enviar.
              </div>
              <button onClick={criarPesquisa} className="btn" style={{ marginTop:16, width:'100%', padding:13 }}>
                ✅ Criar Pesquisa
              </button>
            </div>
          )}

          {/* Enviar pesquisa */}
          {pesquisaTab === 'enviar' && (
            <div className="card" style={{ maxWidth:560 }}>
              <h3 className="card-title">📤 Enviar Pesquisa</h3>
              <div className="field-group" style={{ marginTop:16 }}>
                <label className="r-label">SELECIONAR PESQUISA</label>
                <select className="r-select" value={pesquisaEnvioId} onChange={e => setPesquisaEnvioId(e.target.value)}>
                  <option value="">Selecione...</option>
                  {pesquisas.filter(p => p.ativa).map(p => (
                    <option key={p.id} value={p.id}>{p.titulo}</option>
                  ))}
                </select>
              </div>
              <div className="field-group" style={{ marginTop:12 }}>
                <label className="r-label">SELECIONAR USUÁRIO</label>
                <input className="r-input" placeholder="Buscar nome ou CPF..."
                  onChange={e => {
                    const q = e.target.value.toUpperCase();
                    const found = users.find(u => u.nome?.includes(q) || u.cpf?.includes(q));
                    if (found) setUserEnvio(found.id);
                  }} />
                <select className="r-select" style={{ marginTop:6 }} value={userEnvio} onChange={e => setUserEnvio(e.target.value)}>
                  <option value="">Selecione o usuário...</option>
                  {users.filter(u => u.telefone && u.role !== 'ADMIN' && u.role !== 'AUXILIAR_ADM').map(u => (
                    <option key={u.id} value={u.id}>{u.nome} — {u.telefone}</option>
                  ))}
                </select>
              </div>
              <button onClick={enviarPesquisa} disabled={enviandoPes} className="btn"
                style={{ marginTop:16, width:'100%', padding:13 }}>
                {enviandoPes ? 'Enviando...' : '📤 Enviar Pesquisa via WhatsApp'}
              </button>
            </div>
          )}

          {/* Respostas */}
          {pesquisaTab === 'respostas' && (
            <div>
              <div className="field-group" style={{ marginBottom:16, maxWidth:400 }}>
                <label className="r-label">VISUALIZAR RESPOSTAS DA PESQUISA</label>
                <select className="r-select" value={pesquisaSel || ''}
                  onChange={e => { setPesquisaSel(e.target.value); fetchRespostas(e.target.value); }}>
                  <option value="">Selecione uma pesquisa...</option>
                  {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
                </select>
              </div>
              {respostas.length === 0 && pesquisaSel && (
                <div style={{ textAlign:'center', color:'#aaa', padding:32 }}>Sem respostas ainda.</div>
              )}
              {respostas.length > 0 && (
                <div className="table-wrap">
                  <table className="r-table">
                    <thead>
                      <tr><th>Nome</th><th>CPF</th><th>Município</th><th>Telefone</th><th>Resposta</th><th>Data</th></tr>
                    </thead>
                    <tbody>
                      {respostas.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontSize:13 }}>{r.nome || '—'}</td>
                          <td style={{ fontFamily:'monospace', fontSize:12 }}>{r.cpf || '—'}</td>
                          <td style={{ fontSize:12 }}>{r.municipio || '—'}</td>
                          <td style={{ fontSize:12 }}>{r.telefone || '—'}</td>
                          <td style={{ fontSize:13, fontWeight:600, color:'var(--primary)' }}>{r.resposta}</td>
                          <td style={{ fontSize:11, color:'#888' }}>
                            {new Date(r.created_at).toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
